/**
 * usePeriodoFiltered — calcola tutti i derivati post-filtro per la
 * PaginaPeriodo:
 *  - `exec` (totale/fatti/percentuale) basato sul stato locale ottimistico
 *  - `tappaTargets` / `numericoTargets` filtrati per KR/area/stato
 *  - `filteredMit` / `filteredAzioni` (liste filtrate)
 *  - `filteredMitByData` / `filteredMitByArea` / `filteredAzioniByArea`
 *    (gruppi memoizzati ri-filtrati)
 *  - `filteredMitBySevenDays` (7 secchi giorno per la vista settimana)
 *  - `filtersActive` (flag)
 *
 * Tutta logica pura → side-effect free. Estrazione da `PaginaPeriodo.jsx`
 * (sessione 51) per ridurre la complessità del componente.
 */
import { makePeriodoFilters, makeToggleAreaChip } from './periodoFilters';

export function usePeriodoFiltered({
  data, gran, mit, azioni, targets, sevenDayBuckets,
  filters, setFilters,
  mitByData, mitByArea, azioniByArea,
}) {
  const krs = data?.key_results || [];
  const krById = Object.fromEntries(krs.map((k) => [k.id, k]));

  // ─ Filtri (funzioni predicato) ────────────────────────────────────────
  const { krPassesFilters, azionePassesFilters, mitPassesFilters } =
    makePeriodoFilters(filters, krById);
  const toggleAreaChip = makeToggleAreaChip(filters, setFilters);
  const filtersActive =
    filters.area.length + filters.stato.length + filters.confidence.length > 0;

  // ─ KR tappa/numerico filtrati ─────────────────────────────────────────
  const tappaTargets = targets.filter((t) => {
    const kr = krById[t.key_result_id];
    if (!kr || !(kr.tipo === 'tappa' || kr.tipo === 'abitudine')) return false;
    return krPassesFilters({ ...kr, stato: t.raggiunto ? 'raggiunto' : kr.stato });
  });
  const numericoTargets = targets.filter((t) => {
    const kr = krById[t.key_result_id];
    if (!kr || kr.tipo !== 'numerico') return false;
    return krPassesFilters(kr);
  });

  // ─ Liste filtrate ──────────────────────────────────────────────────────
  const filteredAzioni = azioni.filter(azionePassesFilters);
  const filteredMit = mit.filter(mitPassesFilters);

  // ─ Gruppi memoizzati ri-filtrati ──────────────────────────────────────
  const filteredMitByData = mitByData
    .map(([d, list]) => [d, list.filter(mitPassesFilters)])
    .filter(([, list]) => list.length > 0);
  const filteredMitByArea = mitByArea
    .map(([k, list]) => [k, list.filter(mitPassesFilters)])
    .filter(([, list]) => list.length > 0);
  const filteredAzioniByArea = azioniByArea
    .map(([k, list]) => [k, list.filter(azionePassesFilters)])
    .filter(([, list]) => list.length > 0);

  // Per settimana: garantiamo i 7 secchi (anche vuoti)
  const filteredMitBySevenDays = (() => {
    if (gran !== 'settimana' || !sevenDayBuckets) return null;
    const byDay = new Map(filteredMitByData);
    return sevenDayBuckets.map((iso) => [iso, byDay.get(iso) || []]);
  })();

  // ─ Esecuzione (numero/percentuale) ───────────────────────────────────
  const exec = _computeExec({ gran, mit, azioni, targets, krs });

  return {
    krs, krById,
    filtersActive, toggleAreaChip,
    tappaTargets, numericoTargets,
    filteredMit, filteredAzioni,
    filteredMitByData, filteredMitByArea, filteredAzioniByArea,
    filteredMitBySevenDays,
    exec,
  };
}

/** Calcolo `exec` da stato locale (per ottimismo dopo toggle). */
function _computeExec({ gran, mit, azioni, targets, krs }) {
  let totalCheck = 0;
  let doneCheck = 0;
  if (gran === 'giorno') {
    totalCheck += mit.length;
    doneCheck += mit.filter((m) => m.fatto).length;
  }
  if (['settimana', 'mese', 'quarter', 'anno'].includes(gran)) {
    totalCheck += azioni.length;
    doneCheck += azioni.filter((a) => a.fatto).length;
    const tappaIds = new Set(
      krs.filter((k) => ['tappa', 'abitudine'].includes(k.tipo)).map((k) => k.id),
    );
    const tappaTargs = targets.filter((t) => tappaIds.has(t.key_result_id));
    totalCheck += tappaTargs.length;
    doneCheck += tappaTargs.filter((t) => t.raggiunto).length;
  }
  const percentuale =
    totalCheck > 0 ? Math.round((doneCheck / totalCheck) * 1000) / 10 : null;
  return { totale: totalCheck, fatti: doneCheck, percentuale };
}
